<template>
  <Record
    :id="`RecordArticleLaptop-${laptop.id}`"
    class="RecordArticleLaptop"
    :class="{
      isSelected,
      wrongPrice: articleLaptop && articleLaptop.isPriceCorrect === false,
    }"
    :draggable="!isSelected"
    @dragenter.native.prevent
    @dragover.native.prevent
    @dragstart.native="dragStart($event)"
    @drop.native="dragDrop($event)">
    <div v-if="alternativeTo" slot="header" class="header">
      <Route
        class="Btn ib sm info inv"
        :title="$t('allArticlesOfFlaggedLaptop')"
        :to="{ hash: `#RecordArticleLaptop-${alternativeTo.id}` }">
        {{ $t('alternative', [alternativeTo.name]) }}
      </Route>
      <Btn class="ib sm info" @click="$emit('promote')">
        {{ $t('promote') }}
      </Btn>
    </div>
    <div slot="title" class="title">
      <h4
        @click="copy(laptop.name)"
        @keypress.enter="copy(laptop.name)">
        {{ laptop.name }}
      </h4>
      <LaptopSubtitle
        v-if="isSelected"
        :articleLaptop="articleLaptop"
        :laptop="laptop"
        :topic="topic"
        @update:subtitle="$up('articleLaptop.subtitle', $event)" />
      <LaptopEdit
        :articleLaptop="articleLaptop"
        :isSelected="isSelected"
        :laptop="laptop"
        @patched="$emit('patched')"
        @replace="$emit('openLaptopsSimilarModal')"
        @syncPrice="$emit('syncArticleLaptopPrice')" />
    </div>
    <div class="checkSplit">
      <div class="checkbox">
        <Field
          type="checkbox"
          :value="isSelected"
          @update:value="$emit('update:isSelected', $event)" />
      </div>
      <Row>
        <Column :lg="3">
          <LaptopImage
            :class="{ laptopUnavailableImage: !laptop.price }"
            :laptop="laptop" />
          <Tag v-if="!laptop.price" class="danger inv unavailable">
            {{ $t('unavailable') }}
          </Tag>
        </Column>
        <Column :lg="9">
          <Row>
            <Column
              v-for="{ label, score, scoreClass, value } in laptopSpecs"
              :key="label"
              :md="3"
              :xs="6">
              <LaptopSpec
                :label="label"
                :score="score"
                :scoreClass="scoreClass"
                :value="value" />
            </Column>
            <Set
              icon="link"
              :label="laptop.asin"
              @click="copy(laptop.asin)">
              <a :href="formAmazonLink(laptop.asin)">
                {{ $t('externalLink') }}
              </a>
              <span v-if="laptop.reviewCount" class="reviewRating">
                <Icon name="star" />
                {{ laptop.reviewRating }} ({{ laptop.reviewCount }})
              </span>
            </Set>
          </Row>
        </Column>
      </Row>
    </div>
    <LaptopDescription
      v-if="isSelected"
      :articleLaptop="articleLaptop"
      @update:description="$up('articleLaptop.description', $event)" />
    <LaptopProsAndCons
      v-if="isSelected"
      :articleLaptop="articleLaptop"
      :laptop="laptop"
      @generateProsConsForLaptop="$emit('generateProsConsForLaptop', $event)"
      @update:articleLaptop="$up('articleLaptop', $event)" />
    <LaptopTags
      slot="actions"
      :laptop="laptop" />
    <div v-if="alternatives.length" class="laptopAlternatives">
      <Row>
        <Column
          v-for="laptopAlternative in alternatives"
          :key="laptopAlternative.id"
          :lg="4"
          :md="6"
          :xl="3">
          <LaptopAlternative
            :laptop="laptopAlternative"
            @remove="$emit('removeAlternative', laptopAlternative.id)" />
        </Column>
      </Row>
    </div>
  </Record>
</template>

<script>
import { actions, getters } from '@utils'
import LaptopAlternative from './LaptopAlternative'
import LaptopDescription from './LaptopDescription'
import LaptopEdit from './LaptopEdit'
import LaptopImage from '@components/LaptopImage'
import LaptopProsAndCons from './LaptopProsAndCons'
import LaptopSpec from './LaptopSpec'
import LaptopSubtitle from './LaptopSubtitle'
import LaptopTags from '@components/LaptopTags'

export default {
  components: {
    LaptopAlternative,
    LaptopDescription,
    LaptopEdit,
    LaptopImage,
    LaptopProsAndCons,
    LaptopSpec,
    LaptopSubtitle,
    LaptopTags,
  },
  props: {
    alternativeTo: Object,
    alternatives: Array,
    articleLaptop: Object,
    isSelected: Boolean,
    laptop: Object,
    topic: String,
  },
  computed: {
    hasAlternatives() {
      return this.alternatives.length > 0
    },
    laptopSpecs() {
      const { articleLaptop, laptop } = this
      const { currency, fallback, nth } = this.$options.filters

      const dollars = price => price
        ? currency(price, '$', 0)
        : fallback(price)

      const suffix = (value, suffix) => value
        ? `${value} ${suffix}`
        : fallback()

      return [
        {
          label: this.$t('recommended'),
          score: `#${laptop.bucketIndex + 1}`,
          value: this.$t('under', [dollars(laptop.bucketPrice)]),
        },
        {
          label: this.$t('score'),
          score: laptop.totalScore,
          value: nth(laptop.totalScoreNth),
        },
        {
          label: this.$t('price'),
          score: laptop.valueScore,
          value: dollars(laptop.price || articleLaptop?.price),
        },
        {
          label: this.$t('specTypes.cpu'),
          score: laptop.computingScore,
          scoreClass: 'inv',
          value: laptop.cpu,
        },
        {
          label: this.$t('specTypes.graphics'),
          score: laptop.graphicsScore,
          scoreClass: 'inv',
          value: laptop.gpu,
        },
        {
          label: this.$t('specTypes.memory'),
          score: laptop.memoryScore,
          scoreClass: 'inv',
          value: laptop.memoryAmount,
        },
        {
          label: this.$t('specTypes.storage'),
          score: laptop.storageScore,
          scoreClass: 'inv',
          value: [
            [laptop.ssdAmount, ' GB SSD'],
            [laptop.hddAmount, ' GB HDD'],
          ].filter(([amount]) => amount)
            .map(amountWithSuffix => amountWithSuffix.join(''))
            .join('; '),
        },
        {
          label: this.$t('specTypes.screen'),
          score: laptop.screenScore + laptop.screenQualityScore,
          scoreClass: 'inv',
          value: [
            laptop.screen,
            laptop.resolution,
            laptop.screenNits,
            laptop.screenContrast,
            laptop.screenSrgb,
            laptop.screenRgb,
            laptop.screenP3,
          ].filter(Boolean).join('; '),
        },
        {
          label: this.$t('specTypes.weight'),
          score: laptop.weightScore,
          scoreClass: 'inv',
          value: suffix(laptop.weight, 'kg'),
        },
        {
          label: this.$t('specTypes.battery'),
          score: laptop.batteryScore,
          scoreClass: 'inv',
          value: suffix(laptop.batteryMinutes, 'min'),
        },
        {
          label: this.$t('specTypes.ports'),
          score: laptop.portsScore,
          scoreClass: 'inv',
          value: [
            [laptop.portsHdmi, this.$t('ports.hdmi')],
            [laptop.portsDisplayPort, this.$t('ports.displayPort')],
            [laptop.portsThunderbolt, this.$t('ports.thunderbolt')],
            [laptop.portsUsb3c, this.$t('ports.usb3c')],
            [laptop.portsUsb3a, this.$t('ports.usb3a')],
            [laptop.portsUsb2, this.$t('ports.usb2')],
          ].filter(([portCount]) => portCount)
            .map(port => port.join(' '))
            .join(', '),
        },
      ]
    },
    ...getters('formAmazonLink'),
  },
  methods: {
    copy(message) {
      this.$clipboard(message)
      this.SUCCESS(this.$t('misc.copied'))
    },
    dragStart(event) {
      event.dataTransfer.setData('laptopId', this.laptop.id)
    },
    async dragDrop(event) {
      const laptopDroppedId = parseInt((event.dataTransfer.getData('laptopId')))

      if (this.laptop.id === laptopDroppedId || this.alternativeTo || !this.isSelected) return

      await this.$nextTick()

      this.$emit('addAlternative', laptopDroppedId)
    },
    ...actions(),
  },
}
</script>

<style lang="scss">
.RecordArticleLaptop {
  .checkSplit {
    display: flex;
    flex-wrap: nowrap;

    > .checkbox {
      align-items: center;
      border-right: 1px solid $border-color;
      display: flex;
      margin-right: 1rem;
      min-height: 100%;
      padding-right: 1rem;
    }
  }

  .header {
    margin: 0.5rem 0 0 0.5rem;
  }

  .laptopUnavailableImage {
    opacity: 0.4;
  }

  .Tag.unavailable {
    margin: 0.5rem 0;
    width: 100%;
  }

  .title {
    display: flex;
    justify-content: space-between;
    padding: $uh;
  }

  .prosAndCons {
    margin-top: 0.5rem;
  }

  .laptopAlternatives {
    border-top: solid 0.1rem $info;
    margin-top: 1rem;
    padding-top: 1rem;
  }

  &.isSelected {
    border-color: $info;
  }

  &.wrongPrice {
    border: solid 2px $danger;

    .title {
      .priceAlert {
        display: block;
        font-size: $h7;
        line-height: 1.2rem;
        text-align: center;
      }
    }
  }

}
</style>

<i18n>
{
  "en": {
    "alternative": "Selected as alternative to {0}",
    "allArticlesOfFlaggedLaptop": "See all articles containing this laptop",
    "generate":"Generate",
    "externalLink": "View on Amazon",
    "ports": {
      "hdmi": "HDMI",
      "displayPort": "DisplayPort",
      "thunderbolt": "Thunderbolt",
      "usb3c": "USB 3C",
      "usb3a": "USB 3A",
      "usb2": "USB 2"
    },
    "price": "Price",
    "promote": "Switch to main",
    "recommended": "Recommended",
    "score": "Score",
    "subtitle": "Subtitle",
    "under": "Under {0}",
    "unavailable": "Laptop is unavailable",
    "syncPrice": "Sync price (${0} → ${1})"
  }
}
</i18n>
